import { PATHS } from '@ctcells/core'
import {
  AppErrorScreen,
  AuthLayout,
  ConsoleLayout,
  consoleOpsRoutes,
  consoleResourceRoutes,
  loginScreen,
  NotFoundScreen,
  RequireAuth,
  RouteErrorScreen,
  TabletLayout,
  tabletRoutes,
} from '@ctcells/ui'
import { redirect, type RouteObject } from 'react-router'

/**
 * 통합 콘솔 앱의 라우트 표.
 *
 * 화면 라우트는 `@ctcells/ui` 의 **조각 두 개**로 나뉘어 있다 — 팀원 둘이 같은 파일을
 * 동시에 고치지 않게 팀장이 나눈 것이다. 화면을 더하거나 바꿀 때는 이 파일이 아니라
 * 해당 조각 파일을 고친다.
 *
 *   consoleOpsRoutes      운영(모니터링·작업·이력·알람)
 *   consoleResourceRoutes 리소스(시료·레시피·랩웨어) + 시스템(모듈·로그·관리) + 내 계정
 *
 * 태블릿 kiosk 는 콘솔의 축약 모드라 같은 빌드 안에 있다. 처리 모듈 3종은 장비마다
 * 따로 배포하는 별도 앱이라 여기 없다.
 */
export const routes: RouteObject[] = [
  /*
   * 🔴 **최상위 항목마다 `errorElement` 를 단다**(2026-09-22 실물 확인).
   *    react-router 는 «뿌리 매치»에 자기 기본 경계를 무조건 꽂아서, 비워 두면 셸이 던졌을 때
   *    **「Unexpected Application Error!」 + 영문 스택 전문**이 화면에 깔린다.
   *    `AppErrorScreen` 은 그 자리에 셸 밖 최소 화면(한글)을 세운다 — `RouteErrorScreen` 머리말 참조.
   */

  // 로그인 — 모듈 앱도 같은 셸을 쓴다(@ctcells/ui)
  {
    path: PATHS.login,
    element: <AuthLayout />,
    errorElement: <AppErrorScreen />,
    children: [{ index: true, element: loginScreen }],
  },

  // 통합 콘솔(데스크톱)
  {
    element: (
      <RequireAuth>
        <ConsoleLayout />
      </RequireAuth>
    ),
    // 셸(`ConsoleLayout` · `RequireAuth`)이 던지면 여기가 받는다 — 셸 밖 최소 화면이다
    errorElement: <AppErrorScreen />,
    children: [
      {
        /*
         * 🔴 **렌더 예외도 셸 «안»에서 받는다**(2026-09-22). 이름 없는 레이아웃 라우트 하나를
         *    끼워 그 `errorElement` 로 받는다 — 셸 라우트에 직접 달면 `errorElement` 가
         *    `ConsoleLayout` «을» 대체해서 **헤더도 작업 줄도 E-STOP 도 같이 사라진다.**
         *    한 칸 아래에 두면 셸의 `<Outlet/>` 자리에만 서고 나머지는 살아 있다.
         * ⚠ 우리는 라우트 로더/액션을 쓰지 않는다(자료는 react-query). **렌더 예외 전용**이고,
         *   조회 실패(4xx/5xx)는 각 컨테이너의 `StateNotice` 가 화면 안에서 맡는다.
         */
        errorElement: <RouteErrorScreen />,
        children: [
          { path: '/', loader: () => redirect(PATHS.monitoring) },
          ...consoleOpsRoutes,
          ...consoleResourceRoutes,
          /*
           * 🔴 **없는 주소도 셸 «안»에서 받는다**(2026-09-22 · eric 이 `/history` 로 들어가 본 화면).
           *    바깥에 두면 헤더도 작업 줄도 없는 맨 글자가 뜨고 — **E-STOP 에 닿을 길이 사라진다.**
           *    주소를 잘못 친 것과 안전 장치를 못 쓰는 것은 아무 관계가 없다.
           * ⚠ `/tablet/*` 은 아래 태블릿 가지가 먼저 먹는다. 여기 `*` 는 콘솔 가지 안에서만 남는 것들이다.
           */
          { path: '*', element: <NotFoundScreen /> },
        ],
      },
    ],
  },

  // 태블릿 kiosk — 콘솔 축약형. 실을 화면 목록의 유일한 출처는 @ctcells/ui 의 TABLET_NAV 다
  {
    path: PATHS.tablet,
    element: (
      <RequireAuth>
        <TabletLayout />
      </RequireAuth>
    ),
    errorElement: <AppErrorScreen />,
    // 태블릿도 같은 경계를 탄다 — 라우트 조각(`tabletRoutes`)은 건드리지 않고 여기서 감싼다
    children: [{ errorElement: <RouteErrorScreen />, children: tabletRoutes }],
  },

  /*
   * 🔴 **맨 바깥에 `*` 를 두지 않는다**(2026-09-22). 종전에 여기 있던 한 줄이 바로 eric 이 본
   *    「셸 없는 맨 글자」의 원인이다 — 로그인 여부와 무관하게 이 가지가 먼저 먹었다.
   *    지금은 콘솔 가지 «안»의 `*` 가 받고, 로그인 전이면 그 앞의 `RequireAuth` 가
   *    **로그인으로 보낸 뒤 `state.from` 으로 되돌아온다.** 남길 자리가 없다.
   */
]
